import React, { lazy, Suspense } from 'react';
import { BookOpen, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FOREST_GREEN } from '@/styles/tokens';
import { useKnowledgeStore } from '@/stores/knowledge/knowledge-store';
import ArticleViewer from './ArticleViewer';

const ArticlesTab = lazy(() => import('./ArticlesTab'));
const KnowledgeSearch = lazy(() => import('./KnowledgeSearch'));
const KnowledgeGraphExplorer = lazy(() => import('./KnowledgeGraphExplorer'));
const CompilationDashboard = lazy(() => import('./CompilationDashboard'));
const LintDashboard = lazy(() => import('./LintDashboard'));
const KnowledgeIngestion = lazy(() => import('./KnowledgeIngestion'));
const TrendViewer = lazy(() => import('./TrendViewer'));
const GapDashboard = lazy(() => import('./GapDashboard'));
const ResearchDashboard = lazy(() => import('./ResearchDashboard'));

const TabFallback = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const TABS = [
  ['articles', 'Knowledge'],
  ['search', 'Search'],
  ['graph', 'Graph'],
  ['compile', 'Compile'],
  ['lint', 'Lint'],
  ['feedback', 'Feedback'],
  ['trends', 'Trends'],
  ['gaps', 'Gaps'],
  ['research', 'Research'],
] as const;

function isKnowledgeTab(value: string): value is (typeof TABS)[number][0] {
  return TABS.some(([tab]) => tab === value);
}

export const KnowledgeModal: React.FC = () => {
  const {
    isModalOpen,
    closeModal,
    activeTab,
    setActiveTab,
    viewingArticleSlug,
    currentArticle,
    isLoading,
    error,
    clearError,
    backToList,
  } = useKnowledgeStore();

  return (
    <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
      <DialogContent variant="light" className="sm:max-w-7xl p-0 gap-0 h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-5 pb-3 border-b border-border flex-shrink-0">
          {viewingArticleSlug ? (
            <button
              onClick={backToList}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Knowledge Hub
            </button>
          ) : (
            <>
              <div className="p-1.5 rounded-lg bg-primary/10">
                <BookOpen className="h-5 w-5" style={{ color: FOREST_GREEN.DEFAULT }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Knowledge Hub</h2>
                <p className="text-xs text-muted-foreground">
                  Browse knowledge, search entries, and manage quality
                </p>
              </div>
            </>
          )}
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center justify-between mx-6 mt-3 rounded-md bg-destructive/10 px-4 py-2.5 text-sm text-destructive flex-shrink-0">
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {error}
            </span>
            <button onClick={clearError} className="font-medium underline text-xs">
              Dismiss
            </button>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {viewingArticleSlug ? (
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {isLoading ? (
                <TabFallback />
              ) : currentArticle ? (
                <ArticleViewer article={currentArticle} />
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Article not found.</p>
                </div>
              )}
            </div>
          ) : (
            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                if (isKnowledgeTab(value)) {
                  setActiveTab(value);
                }
              }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <div className="px-6 pt-3 flex-shrink-0">
                <TabsList className="grid w-full grid-cols-9 h-auto p-1 bg-muted rounded-lg">
                  {TABS.map(([value, label]) => (
                    <TabsTrigger
                      key={value}
                      value={value}
                      className="py-2 rounded-md text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-all"
                    >
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              <div className="flex-1 overflow-y-auto">
                <TabsContent value="articles" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <ArticlesTab />
                  </Suspense>
                </TabsContent>

                <TabsContent value="search" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <KnowledgeSearch />
                  </Suspense>
                </TabsContent>

                <TabsContent value="graph" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <KnowledgeGraphExplorer />
                  </Suspense>
                </TabsContent>

                <TabsContent value="compile" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <CompilationDashboard />
                  </Suspense>
                </TabsContent>

                <TabsContent value="lint" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <LintDashboard />
                  </Suspense>
                </TabsContent>

                <TabsContent value="feedback" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <KnowledgeIngestion />
                  </Suspense>
                </TabsContent>

                <TabsContent value="trends" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <TrendViewer />
                  </Suspense>
                </TabsContent>

                <TabsContent value="gaps" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <GapDashboard />
                  </Suspense>
                </TabsContent>

                <TabsContent value="research" className="px-6 py-4 mt-0">
                  <Suspense fallback={<TabFallback />}>
                    <ResearchDashboard />
                  </Suspense>
                </TabsContent>
              </div>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeModal;
